import { CheckCircle2, Eye, Mail, Users } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { MetricCard } from "@/components/shared/metric-card";
import { useNotificationInsights } from "../use-notification-insights";

// Phase 7 sub-bucket 19 — 30-day notification InsightsBar.
//
// Ports the legacy /app/admin/settings/notifications/InsightsBar surface
// (mails sent / opens / unique partners reached / unique people reached)
// backed by `loadNotificationInsights` via the new
// /api/settings/notifications/insights endpoint.
//
// Mounts above the rules table on /admin/settings/notifications. Refreshes
// auto-magically on rule mutations via the four notification-rule hooks
// invalidating `queryKeys.settings.notificationInsights()`.

export function InsightsBar() {
  const query = useNotificationInsights();

  if (query.isLoading) {
    return (
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (query.isError || !query.data) return null;

  const { emailsSent, emailsOpened, uniquePartnersReached, uniquePeopleReached } =
    query.data;

  // Open rate guard — when no emails were sent we surface "—" rather than
  // 0/0 = NaN. Two decimal-point precision matches the legacy InsightsBar.
  const openRate =
    emailsSent === 0
      ? "—"
      : `${((emailsOpened / emailsSent) * 100).toFixed(1)}%`;

  return (
    <section aria-label="30-day notification insights" className="mb-6">
      <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Last 30 days
      </h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Emails sent"
          value={String(emailsSent)}
          icon={Mail}
        />
        <MetricCard
          label="Open rate"
          value={openRate}
          icon={Eye}
          hint={
            emailsSent === 0
              ? undefined
              : `${emailsOpened} opens of ${emailsSent}`
          }
        />
        <MetricCard
          label="Partners reached"
          value={String(uniquePartnersReached)}
          icon={Users}
        />
        <MetricCard
          label="People reached"
          value={String(uniquePeopleReached)}
          icon={CheckCircle2}
        />
      </div>
    </section>
  );
}
